/**
 * Portfolio tab: counts for projects, people, inchstones, risks, issues,
 * actions, plus spend (budget vs calculated cost) and benefits KPIs.
 */

import React from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fmt, fmtMoney, gaugeFill, kpiGridClass, type PortfolioMetrics } from './types';

interface PortfolioChartsProps {
  metrics: PortfolioMetrics;
  columns: number;
  t: (key: string) => string;
}

function Panel({
  title,
  hint,
  children
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <article className="border border-[var(--color-border)] rounded p-2.5 bg-[var(--color-secondary-bg)]">
      <h3 className="text-sm font-medium text-[var(--color-surface-text)] mb-1">{title}</h3>
      {children}
      {hint ? <p className="text-[10px] text-[var(--color-secondary-text)] mt-1 leading-tight">{hint}</p> : null}
    </article>
  );
}

function HorizontalBars({
  rows,
  colors,
  formatValue = fmt
}: {
  rows: Array<{ name: string; value: number }>;
  colors: string[];
  formatValue?: (value: number | null | undefined) => string;
}) {
  return (
    <div className="h-28">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={rows}
          margin={{ top: 4, right: 40, left: 8, bottom: 4 }}
        >
          <XAxis type="number" hide domain={[0, 'auto']} />
          <YAxis
            type="category"
            dataKey="name"
            width={88}
            tick={{ fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{ fontSize: 11 }}
            formatter={(value: number) => [formatValue(value), '']}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14} isAnimationActive={false}>
            {rows.map((_, i) => (
              <Cell key={rows[i].name} fill={colors[i % colors.length]} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              formatter={(value: number) => formatValue(value)}
              style={{ fontSize: 10, fill: 'var(--color-surface-text)' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export const PortfolioCharts: React.FC<PortfolioChartsProps> = ({ metrics, columns, t }) => {
  const spendColor =
    metrics.spendTarget <= 0
      ? null
      : metrics.spendActual <= metrics.spendTarget
        ? 'GREEN'
        : metrics.spendActual <= metrics.spendTarget * 1.2
          ? 'YELLOW'
          : 'RED';

  const benefitsColor =
    metrics.benefitsTarget <= 0
      ? null
      : metrics.benefitsActual >= metrics.benefitsTarget
        ? 'GREEN'
        : metrics.benefitsActual >= metrics.benefitsTarget * 0.8
          ? 'YELLOW'
          : 'RED';

  const primary = '#004D99';
  const muted = '#95C4FF';
  const closedGrey = '#9CA3AF';
  const flow = [primary, gaugeFill('GREEN'), gaugeFill('RED')];
  const projectColors = [primary, closedGrey, gaugeFill('GREEN')];

  return (
    <div className={`grid gap-2 items-start ${kpiGridClass(columns)}`}>
      <Panel title={t('kpi.perfProjects') || 'Projects'}>
        <HorizontalBars
          rows={[
            { name: t('kpi.perfCreated') || 'Created', value: metrics.projectsCreated },
            { name: t('kpi.perfClosed') || 'Closed', value: metrics.projectsClosed },
            { name: t('kpi.perfOpen') || 'Open', value: metrics.projectsOpen }
          ]}
          colors={projectColors}
        />
      </Panel>

      <Panel
        title={t('kpi.perfPeople') || 'People on projects'}
        hint={t('kpi.perfPeopleHint') || 'Distinct owners, managers, and members on those projects.'}
      >
        <p className="text-2xl font-semibold text-[var(--color-surface-text)] leading-none py-4">
          {fmt(metrics.peopleOnProjects)}
        </p>
      </Panel>

      <Panel title={t('kpi.perfInchstones') || 'Inchstones'}>
        <HorizontalBars
          rows={[
            { name: t('kpi.perfDue') || 'Due', value: metrics.inchstonesDue },
            { name: t('kpi.perfAchieved') || 'Achieved', value: metrics.inchstonesAchieved },
            { name: t('kpi.perfDelayed') || 'Delayed', value: metrics.inchstonesDelayed }
          ]}
          colors={[primary, gaugeFill('GREEN'), gaugeFill('RED')]}
        />
      </Panel>

      <Panel title={t('kpi.perfRisks') || 'Risks'}>
        <HorizontalBars
          rows={[
            { name: t('kpi.perfAdded') || 'Added', value: metrics.risksAdded },
            { name: t('kpi.perfClosed') || 'Closed', value: metrics.risksClosed },
            { name: t('kpi.perfOutstanding') || 'Outstanding', value: metrics.risksOutstanding }
          ]}
          colors={flow}
        />
      </Panel>

      <Panel title={t('kpi.perfIssues') || 'Issues'}>
        <HorizontalBars
          rows={[
            { name: t('kpi.perfAdded') || 'Added', value: metrics.issuesAdded },
            { name: t('kpi.perfClosed') || 'Closed', value: metrics.issuesClosed },
            { name: t('kpi.perfOutstanding') || 'Outstanding', value: metrics.issuesOutstanding }
          ]}
          colors={flow}
        />
      </Panel>

      <Panel title={t('kpi.perfActions') || 'Actions'}>
        <HorizontalBars
          rows={[
            { name: t('kpi.perfAdded') || 'Added', value: metrics.actionsAdded },
            { name: t('kpi.perfClosed') || 'Closed', value: metrics.actionsClosed },
            { name: t('kpi.perfOutstanding') || 'Outstanding', value: metrics.actionsOutstanding }
          ]}
          colors={flow}
        />
      </Panel>

      <Panel
        title={t('kpi.perfSpend') || 'Project spend'}
        hint={
          t('kpi.perfSpendHint') ||
          'Budget vs calculated cost on projects in your tenant and hierarchy.'
        }
      >
        <HorizontalBars
          rows={[
            { name: t('kpi.perfBudget') || 'Budget', value: metrics.spendTarget },
            { name: t('kpi.perfCalculatedCost') || 'Calculated cost', value: metrics.spendActual }
          ]}
          colors={[muted, gaugeFill(spendColor)]}
          formatValue={fmtMoney}
        />
      </Panel>

      <Panel
        title={t('kpi.perfBenefits') || 'Benefits / productivity'}
        hint={
          t('kpi.perfBenefitsHint') ||
          'PROJECT KPI targets and latest actuals whose names include benefit or productivity.'
        }
      >
        <HorizontalBars
          rows={[
            { name: t('kpi.target') || 'Target', value: metrics.benefitsTarget },
            { name: t('kpi.actual') || 'Actual', value: metrics.benefitsActual }
          ]}
          colors={[muted, gaugeFill(benefitsColor)]}
          formatValue={fmtMoney}
        />
      </Panel>
    </div>
  );
};
